import React, { useState, useContext, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { authFetch, fetchCsrfToken } from './authFetch';
import { UserContext } from './App';

// Production mode check - disable verbose logging in production
const IS_DEV = process.env.NODE_ENV === 'development';
const devLog = (message) => { if (IS_DEV) console.log(`[Login] ${message}`); };

function Login() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [otp_token, setOtpToken] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [oauthConfig, setOauthConfig] = useState({ enabled: false });
  const [oauthLoading, setOauthLoading] = useState(true);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const { setCurrentUser } = useContext(UserContext) || {};
  const BASE_URL = process.env.REACT_APP_API_BASE_URL;

  // Check for OAuth errors and session timeout in URL params
  useEffect(() => {
    const error = searchParams.get('error');
    const detail = searchParams.get('detail');

    if (error) {
      let errorMessage = 'Login failed. Please try again.';

      switch (error) {
        case 'session_timeout':
          errorMessage = 'Your session has expired due to inactivity. Please log in again.';
          break;
        case 'oauth_failed':
          errorMessage = `Office 365 login failed: ${detail || 'Unknown error'}`;
          break;
        case 'no_code':
          errorMessage = 'Office 365 login was cancelled or failed.';
          break;
        case 'auth_failed':
          errorMessage = 'Authentication failed. Please contact your administrator.';
          break;
        case 'callback_failed':
          errorMessage = 'Login process failed. Please try again.';
          break;
        case 'fetch_user_failed':
          errorMessage = 'Login succeeded but failed to fetch user profile.';
          break;
        case 'callback_error':
          errorMessage = 'OAuth callback error occurred.';
          break;
        default:
          break;
      }

      setError(errorMessage);
    }
  }, [searchParams]);

  // Load OAuth configuration
  useEffect(() => {
    const loadOAuthConfig = async () => {
      try {
        setOauthLoading(true);
        devLog('Loading OAuth config');

        const response = await fetch(`${BASE_URL}/api/oauth/config/`);

        if (response.ok) {
          const config = await response.json();
          devLog('OAuth config loaded');
          setOauthConfig(config);
        } else {
          devLog('OAuth config endpoint unavailable');
          setOauthConfig({ enabled: false });
        }
      } catch (err) {
        devLog('Failed to load OAuth config');
        setOauthConfig({ enabled: false });
      } finally {
        setOauthLoading(false);
      }
    };

    loadOAuthConfig();
  }, [BASE_URL]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      devLog('Initiating login');
      await fetchCsrfToken();

      const loginResponse = await authFetch(`${BASE_URL}/api/auth/login/`, {
        method: 'POST',
        body: JSON.stringify({
          email: username.toLowerCase(),
          password,
          otp_token
        })
      });

      if (!loginResponse.ok) {
        const data = await loginResponse.json();
        throw new Error(data.error || 'Login failed. Please check your credentials.');
      }

      devLog('Login successful');
      await loginResponse.json();

      // Get user profile using session authentication
      const userResponse = await authFetch(`${BASE_URL}/api/auth/user/`);

      if (userResponse.ok) {
        const userData = await userResponse.json();

        const userDetailResponse = await authFetch(`${BASE_URL}/api/auth/debug/`);

        if (userDetailResponse.ok) {
          const userDetails = await userDetailResponse.json();

          const enrichedUserData = {
            ...userData,
            id: userDetails.id,
            groups: userDetails.groups,
            is_staff: userDetails.is_staff,
            is_superuser: userDetails.is_superuser,
            authType: 'session'
          };

          devLog('User profile loaded');

          if (setCurrentUser) {
            setCurrentUser(enrichedUserData);
          }

          localStorage.setItem('userDetails', JSON.stringify(enrichedUserData));

        } else {
          const basicUserData = {
            ...userData,
            authType: 'session'
          };

          if (setCurrentUser) {
            setCurrentUser(basicUserData);
          }
          localStorage.setItem('userDetails', JSON.stringify(basicUserData));
        }

        // Get API token for scanner app
        try {
          const tokenResponse = await authFetch(`${BASE_URL}/api/auth/token/`);

          if (tokenResponse.ok) {
            const tokenData = await tokenResponse.json();
            devLog('API token retrieved');

            // Store token in localStorage for scanner app
            localStorage.setItem('apiToken', tokenData.token);
          }
        } catch (tokenError) {
          // Don't fail the login if token fetch fails
          devLog('API token fetch failed (non-critical)');
        }

        navigate('/');
      } else {
        throw new Error('Login succeeded but failed to retrieve your user profile.');
      }
    } catch (err) {
      devLog('Login error');
      setError(err.message || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleOffice365Login = async () => {
    try {
      setError('');
      setLoading(true);

      devLog('Initiating Office 365 login');
      const response = await fetch(`${BASE_URL}/api/oauth/login/`);
      const data = await response.json();

      if (response.ok && data.oauth_url) {
        devLog('Redirecting to Office 365');
        window.location.href = data.oauth_url;
      } else {
        throw new Error(data.error || 'Failed to initiate Office 365 login');
      }
    } catch (err) {
      devLog('Office 365 login error');
      setError(err.message || 'Office 365 login failed. Please try again.');
      setLoading(false);
    }
  };

  if (oauthLoading) {
    return (
      <div className="max-w-md mx-auto mt-12 p-8 bg-white rounded shadow text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
        <p>Loading login options...</p>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto mt-12 p-8 bg-white rounded shadow">
      <h2 className="text-2xl font-bold mb-6 text-center">Login</h2>
      {error && (
        <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
          {error}
        </div>
      )}
      
      {/* Office 365 Login Button */}
      {oauthConfig.enabled && (
        <div className="mb-6">
          <button
            onClick={handleOffice365Login}
            disabled={loading}
            className={`w-full py-3 px-4 rounded text-white font-medium transition-colors flex items-center justify-center space-x-2 ${
              loading 
                ? 'bg-gray-400 cursor-not-allowed' 
                : 'bg-blue-600 hover:bg-blue-700'
            }`}
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
              <path d="M23.5 12c0-6.351-5.149-11.5-11.5-11.5S.5 5.649.5 12 5.649 23.5 12 23.5 23.5 18.351 23.5 12zM12 22C6.201 22 1.5 17.299 1.5 12S6.201 2 12 2s10.5 4.701 10.5 10.5S17.799 22 12 22z"/>
              <path d="M11.5 6.5h-4v4h4v-4zm0 11h-4v-4h4v4zm5-11h-4v4h4v-4zm0 11h-4v-4h4v4z"/>
            </svg>
            <span>{loading ? 'Redirecting...' : 'Sign in with Office 365'}</span>
          </button>
          
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white text-gray-500">Or continue with username</span>
            </div>
          </div>
        </div>
      )}
      
      {/* Regular Login Form with 2FA */}
      <form onSubmit={handleSubmit} className="space-y-4">
        <input
          type="text"
          placeholder="Username or Email"
          className="w-full border px-3 py-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          required
          disabled={loading}
          autoComplete="username"
        />
        <input
          type="password"
          placeholder="Password"
          className="w-full border px-3 py-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          disabled={loading}
          autoComplete="current-password"
        />
        <input
          type="text"
          placeholder="2FA Code"
          className="w-full border px-3 py-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
          value={otp_token}
          onChange={(e) => setOtpToken(e.target.value)}
          required
          disabled={loading}
          autoComplete="one-time-code"
        />
        <button 
          type="submit" 
          className={`w-full py-2 rounded text-white transition-colors ${
            loading 
              ? 'bg-gray-400 cursor-not-allowed' 
              : 'bg-gray-600 hover:bg-gray-700'
          }`}
          disabled={loading}
        >
          {loading ? 'Logging in...' : 'Log In'}
        </button>
      </form>
    </div>
  );
}

export default Login;